import React, { useEffect, useState } from 'react';
import './ChevronProcessDiagram.scss';
import { ChevronDiagramEvent } from './models/ChevronDiagramEvent';
import { formatBusinessDatePt } from '../../../slices/marketplace/timeline/utils/timeline-dates';
import {
    TimelineParties,
    TimelineParty,
} from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import {
    ActionTypeDefinition,
    MarketplaceEventActionTypeList,
    TimelineActionType,
    getActionTypeName,
} from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { DocumentDetailsContentType } from '../../../slices/marketplace/timeline/models/enums/document-details-content-type';
import { LevelOfService, isSfas } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { ExtendDeadlinePopup } from '../../ConnectMarketplaceDashboard/NdaTermsPopup/ExtendDeadlinePopup/ExtendDeadlinePopup';
import { useStateSelector } from '../../../store/selectors';
import { useDispatch } from 'react-redux';
import { GetTimelineById } from '../../../slices/marketplace/timeline/thunks/get-timeline-by-id.thunk';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { flagEventId, marketplaceFlagCodes } from '../../../notifications/notification-flag-codes';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import createNotification from '../../../utils/createNotification';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import { canSellerConcludeMeetingsPeriod } from '../../ConnectMarketplaceDashboard/EventActionAvailabilityHelper';
import { CompleteEngagementEvent } from '../../../slices/marketplace/timeline/thunks/complete-engagement-event.thunk';

interface ChevronDiagramEventProps {
    chevronDiagramEvent: ChevronDiagramEvent;
    onActionClick(item: ChevronDiagramEvent): void;
    isEventActionVisible: (eventId: string) => boolean;
    party: TimelineParty;
    engagementId: number;
    isBuyerView: boolean;
    timelineId?: number;
    hideExecuteButton: boolean;
}

export const ChevronDiagramTimelineEvent = (
    props: ChevronDiagramEventProps
) => {
    const dispatch = useDispatch() as any;
    const { chevronDiagramEvent: chevronDiagramEvent } = props;
    const partyName = TimelineParties.find(
        (p) => p.id === chevronDiagramEvent.party
    )?.name;
    const [isExtendDeadlinePopupVisible, setIsExtendDeadlinePopupVisible] =
        useState<boolean>(false);
    const [isMarkAsCompleteConfirmVisible, setIsMarkAsCompleteConfirmVisible] =
        useState<boolean>(false);
    const [isMarkAsCompleteLoading, setIsMarkAsCompleteLoading] =
        useState<boolean>(false);
    const [isConcludeStepLoading, setIsConcludeStepLoading] =
        useState<boolean>(false);
    const [isConcludeStepConfirmVisible, setIsConcludeStepConfirmVisible] =
        useState<boolean>(false);
    const currentEngagement = useStateSelector(
        (s) => s.timelines.currentTimeline
    );
    const engagementMeetings = useStateSelector(
        (s) => s.engagementMeetings.engagementMeetings
    );
    const areEngagementMeetingsLoading = useStateSelector(
        (s) => s.engagementMeetings.isLoading
    );
    const axios = useStateSelector((s) => s.core.axios);

    const [action, setAction] = useState<ActionTypeDefinition>(null);
    const [notificationFlagCode, setNotificationFlagCode] = useState<string>();

    useEffect(() => {
        const actionType = chevronDiagramEvent.linkedAction?.actionType;
        const foundAction = MarketplaceEventActionTypeList.find(
            (a) => a.id === actionType
        );
        setAction(foundAction || null);
    }, []);

    useEffect(() => {
        setNotificationFlagCode(
            marketplaceFlagCodes.timelinePreview(
                { engagementId: props.engagementId, timelineId: props.timelineId },
                props.isBuyerView
            )
        );
    }, [props.engagementId]);

    const isEventActionVisible = !props.isEventActionVisible
        ? true
        : props.isEventActionVisible(props.chevronDiagramEvent.id);

    const mapActionName = (): string => {
        if (chevronDiagramEvent.actionLinkText)
            return chevronDiagramEvent.actionLinkText;

        if (action.id === TimelineActionType.SellerEngagementDocument) {
            const contentType =
                chevronDiagramEvent.linkedAction?.documentContentType;
            if (contentType === DocumentDetailsContentType.PipOrPiq) {
                switch (currentEngagement?.levelOfService) {
                    case LevelOfService.FullServiceBrokerage:
                        return 'Review & Approve PIP';
                    case LevelOfService.StrategicFinancialAdvisoryService:
                        return 'Review & Approve CIP';
                    default:
                        return 'Review & Approve PIQ';
                }
            }
            if (contentType === DocumentDetailsContentType.FinancialModel) {
                return 'Review & Approve DFM';
            }
        }

        if (action.oppositePartyName) {
            if (action.party === TimelineParty.SellerAndBuyer) {
                return props.party === TimelineParty.Buyer
                    ? action.oppositePartyName
                    : getActionTypeName(action.id);
            }
            if (action.party !== props.party) return action.oppositePartyName;
        }
        return getActionTypeName(action.id);
    };

    const isCurrentEvent = () =>
        chevronDiagramEvent.status === TimelineEventStatus.InProgress ||
        chevronDiagramEvent.status === TimelineEventStatus.Late;

    const isBuyerRelatedEvent = () =>
        chevronDiagramEvent.party === TimelineParty.Buyer ||
        chevronDiagramEvent.linkedAction?.actionType ===
            TimelineActionType.SellerBuyerSelectionIdNda ||
        chevronDiagramEvent.linkedAction?.actionType ===
            TimelineActionType.SellerCustomSelectDataNdaTerms;

    const reloadCurrentEngagement = () => {
        dispatch(
            GetTimelineById({ id: currentEngagement.id, isTemplate: false })
        ).then();
    };

    const isSfasBroker =
        props.party === TimelineParty.Broker &&
        isSfas(currentEngagement?.levelOfService);

    const handleMarkAsComplete = () => {
        setIsMarkAsCompleteLoading(true);
        axios
            .put(
                `/api/Marketplace/Engagements/${currentEngagement.id}/CompleteEvent/${chevronDiagramEvent.id}`
            )
            .then(() => {
                reloadCurrentEngagement();
                setIsMarkAsCompleteConfirmVisible(false);
            })
            .catch((error) => {
                const message = error?.response?.data?.title || 'Failed to complete the step. Please try again.';
                createNotification(message, 'error');
            })
            .finally(() => {
                setIsMarkAsCompleteLoading(false);
            });
    };

    const canConcludeMeetingsPeriod =
        !props.hideExecuteButton &&
        !areEngagementMeetingsLoading &&
        canSellerConcludeMeetingsPeriod(
            chevronDiagramEvent,
            props.party,
            engagementMeetings
        );

    const handleConcludeStep = () => {
        setIsConcludeStepLoading(true);
        dispatch(
            CompleteEngagementEvent({
                engagementId: currentEngagement.id,
                eventId: chevronDiagramEvent.id,
            })
        )
            .unwrap()
            .then(() => {
                reloadCurrentEngagement();
                setIsConcludeStepConfirmVisible(false);
            })
            .catch(() => {})
            .finally(() => {
                setIsConcludeStepLoading(false);
            });
    };

    const isSuspendEvent = () => {
        return chevronDiagramEvent.isSuspendEvent === true;
    };

    const formatCompletedDate = (date: Date | null) => {
        if (!date) return '';
        return formatBusinessDatePt(date);
    };

    const formatDeadlineDate = (): string =>
        formatBusinessDatePt(chevronDiagramEvent.deadline);

    const renderDeadlineText = (): React.JSX.Element => {
        if (chevronDiagramEvent.status === TimelineEventStatus.Completed) {
            return (
                <>
                    Date completed:{' '}
                    {formatCompletedDate(chevronDiagramEvent.dayCompleted)}
                </>
            );
        }

        if (isSuspendEvent()) {
            return <></>;
        }

        return <>Deadline: {formatDeadlineDate()}</>;
    };

    return (
        <div>
            <div
                className={`column-item
                ${isSuspendEvent() ? 'suspended-event' : ''} ${
                    chevronDiagramEvent.status === TimelineEventStatus.Upcoming
                        ? 'upcoming-event'
                        : ''
                } ${
                    chevronDiagramEvent.status === TimelineEventStatus.Late
                        ? 'late-event'
                        : ''
                } ${
                    chevronDiagramEvent.status ===
                    TimelineEventStatus.InProgress
                        ? 'in-progress-event'
                        : ''
                }`}>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        {isSuspendEvent() ? (
                            <></>
                        ) : (
                            <strong>{partyName}</strong>
                        )}
                    </div>
                    <NotificationFlag
                        eventCode={`${notificationFlagCode}.${flagEventId(chevronDiagramEvent)}`}
                        includePrefix={true}
                        flagClassName={'event-notofication-flag'}
                    />
                </div>
                <div className="event-name">{chevronDiagramEvent.name}</div>
                <div className="start-date">
                    Start Date:{' '}
                    {formatBusinessDatePt(chevronDiagramEvent.dayStart)}
                </div>

                <div className="deadline">
                    {renderDeadlineText()}
                    {action && isEventActionVisible && (
                        <div
                            className={`linked-action ${
                                chevronDiagramEvent.status ===
                                TimelineEventStatus.Completed
                                    ? 'completed-linked-action'
                                    : ''
                            }`}
                            onClick={() =>
                                props.onActionClick(chevronDiagramEvent)
                            }>
                            {mapActionName()}
                        </div>
                    )}
                    {!props.hideExecuteButton &&
                    props.party === TimelineParty.Seller &&
                    isCurrentEvent() &&
                    isBuyerRelatedEvent() ? (
                        <div
                            className="linked-action"
                            onClick={() =>
                                setIsExtendDeadlinePopupVisible(true)
                            }>
                            Extend Deadline
                        </div>
                    ) : (
                        []
                    )}
                    {isSfasBroker &&
                        chevronDiagramEvent.status ===
                            TimelineEventStatus.InProgress && (
                            <button
                                className="btn btn-primary btn-mark-as-complete"
                                onClick={() =>
                                    setIsMarkAsCompleteConfirmVisible(true)
                                }>
                                Mark Step as Complete
                            </button>
                        )}
                    {canConcludeMeetingsPeriod && (
                        <div className="conclude-step-action">
                            <button
                                className="btn btn-primary btn-mark-as-complete"
                                onClick={() =>
                                    setIsConcludeStepConfirmVisible(true)
                                }>
                                Conclude this step
                            </button>
                            <InfoTooltip
                                idText={`conclude-step-tooltip-${chevronDiagramEvent.id}`}
                                text="All scheduled meetings for this step have passed. Would you like to conclude this step now? The next step will begin immediately."
                            />
                        </div>
                    )}
                </div>
            </div>
            {isExtendDeadlinePopupVisible ? (
                <ExtendDeadlinePopup
                    isVisible={isExtendDeadlinePopupVisible}
                    setIsVisible={setIsExtendDeadlinePopupVisible}
                    eventId={chevronDiagramEvent.id}
                    eventDeadline={chevronDiagramEvent.deadline}
                    engagementId={currentEngagement.id}
                    fetchData={reloadCurrentEngagement}
                    engagementProcess={currentEngagement.engagementProcess}
                    levelOfService={currentEngagement.levelOfService}
                    mode={'general'}
                />
            ) : (
                <></>
            )}
            <ConfirmationModal
                isVisible={isConcludeStepConfirmVisible}
                setIsVisible={setIsConcludeStepConfirmVisible}
                className="conclude-step-modal"
                header={`Are you sure?`}
                confirmationText={
                    <>
                        <p>
                            Are you sure you want to conclude{' '}
                            <b>{chevronDiagramEvent.name}</b> now?
                        </p>
                        <p>
                            All scheduled meetings for this step have passed.
                            Concluding is irreversible and the next step will
                            begin immediately.
                        </p>
                    </>
                }
                nextButtonText="Conclude"
                cancelButtonText="Cancel"
                isLoading={isConcludeStepLoading}
                closeOnConfirm={false}
                onConfirm={handleConcludeStep}
            />
            <ConfirmationModal
                isVisible={isMarkAsCompleteConfirmVisible}
                setIsVisible={setIsMarkAsCompleteConfirmVisible}
                className="mark-step-as-complete-modal"
                header={`Are you sure?`}
                confirmationText={
                    <>
                        <p>
                            Are you sure, that you want to complete{' '}
                            <b>{chevronDiagramEvent.name}</b>?
                        </p>
                        <p>
                            Doing so is irreversible and will make live any
                            steps that are dependent on this step to be
                            complete.
                        </p>
                    </>
                }
                nextButtonText="Mark as Complete"
                cancelButtonText="Cancel"
                isLoading={isMarkAsCompleteLoading}
                closeOnConfirm={false}
                onConfirm={handleMarkAsComplete}
            />
        </div>
    );
};
